import { Input } from '../Input.tsx';
import { Icon24 } from '../icons/Icon.tsx';
import * as Icons24 from 'dg-web-shared/ui/icons/Icons24.tsx';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import { texts } from './SearchPlaceholder.tsx';
import { css } from '@emotion/css';
import { OperatorTypo } from '../OperatorTypo.ts';
import { ColorHex } from '../Colors.ts';
import { defaultTransition } from 'dg-web-shared/tb-ui/transitions.ts';
import { type JSX } from 'react';

interface Props {
    searchValue: string;
    language: string;
    onChange: (value: string) => void;
}

export function SearchField(props: Props) {
    function renderActionIcon(): JSX.Element {
        function getActionIconStyle(hoverEffect: boolean) {
            return css({
                color: ColorHex.white,
                position: 'absolute',
                right: '-8px',
                top: '12px',
                padding: '4px',
                '&:hover': {
                    background: hoverEffect ? ColorHex.lightblue : undefined,
                },
            });
        }

        if (props.searchValue) {
            return (
                <Clickable
                    element="div"
                    className={getActionIconStyle(true)}
                    onClick={() => {
                        props.onChange('');
                    }}
                >
                    <Icon24 icon={Icons24.clear_m} />
                </Clickable>
            );
        } else {
            return (
                <div className={getActionIconStyle(false)}>
                    <Icon24 icon={Icons24.search_m} />
                </div>
            );
        }
    }

    return (
        <div
            className={css({
                background: ColorHex.darkblue,
                height: '100%',
                borderRight: `2px solid ${ColorHex.white}`,
            })}
        >
            <div
                className={css({
                    position: 'relative',
                    marginLeft: '24px',
                    marginRight: '24px',
                    height: '100%',
                })}
            >
                <label
                    className={css({
                        height: '16px',
                        position: 'relative',
                        display: 'block',
                        cursor: 'default',
                        overflow: 'visible',
                    })}
                >
                    <span
                        className={css({
                            display: 'block',
                            position: 'absolute',
                            // `focused` was component state that was
                            // initialised to true and never written, so
                            // this was always 'default'.
                            cursor: 'default',
                            ...defaultTransition('transform'),
                            color: ColorHex.white,
                            top: '7px',
                            ...OperatorTypo.label,
                        })}
                    >
                        {texts[props.language].Search()}
                    </span>
                </label>
                <div
                    className={css({
                        position: 'absolute',
                        left: 0,
                        top: '18px',
                        right: '56px',
                        color: ColorHex.white,
                    })}
                >
                    <Input
                        className={css({
                            color: ColorHex.white,
                            height: '24px',
                            ...OperatorTypo.headingOne,
                            userSelect: 'text',
                            margin: 0,
                            padding: 0,
                            border: 'none',
                            outline: 'none',
                            display: 'block',
                            verticalAlign: 'baseline',
                            whiteSpace: 'normal',
                            background: 'none',
                            width: '100%',
                            lineHeight: 1,
                            '&::-ms-clear': {
                                display: 'none',
                            },
                        })}
                        inputType="text"
                        maxChars={80}
                        autoCapitalize={undefined}
                        autoCorrect={undefined}
                        onBlur={(): void => {
                            /*noop*/
                        }}
                        onFocus={(): void => {
                            /*noop*/
                        }}
                        onChange={props.onChange}
                        value={props.searchValue}
                        tabIndex={1}
                        autoFocus
                    />
                </div>
                <div
                    className={css({
                        height: '1px',
                        background: ColorHex.rgba(ColorHex.white, 0.25),
                        position: 'absolute',
                        bottom: '9px',
                        left: 0,
                        right: 0,
                    })}
                />
                {renderActionIcon()}
            </div>
        </div>
    );
}
